/**
 * MarketingGlossary.jsx — Track B, Phase 12a (light theme).
 *
 * A full listing page (not a modal) that surfaces every metric defined
 * in `metric_dictionary.js` grouped by category. Each row uses the same
 * unified `MetricExplainer` used by segments — no divergent wording.
 *
 * Owner directive 2026-07-24:
 *   • Cream/gold theme matching the rest of the app.
 *   • Explain every marketing indicator (retention, ATV, churn, …) in
 *     plain Arabic that any non-technical user can read.
 *
 * ISOLATION: mounted only at hidden route `/marketing-glossary-preview`.
 * Zero touch on tabs.js or App.js.
 */
import React, { useMemo, useState } from "react";
import { METRIC_DICTIONARY } from "./metric_dictionary";
import MetricExplainer from "./MetricExplainer";

const CATEGORY_META = {
  kpi:       { emoji: "\u{1F4CA}", label: "مؤشرات الأداء الرئيسية" },
  ratio:     { emoji: "\u{1F4C8}", label: "نِسَب ومعدّلات" },
  segments:  { emoji: "\u{1F3AF}", label: "شرائح العملاء" },
  sources:   { emoji: "\u{1F504}", label: "مصادر البيانات" },
  benchmark: { emoji: "\u{1F3C6}", label: "معايير مقارنة" },
};

export default function MarketingGlossary() {
  const [open, setOpen] = useState({ cat: null, id: null });
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    const q = filter.trim();
    return Object.entries(CATEGORY_META).map(([cat, meta]) => {
      const entries = Object.entries(METRIC_DICTIONARY[cat] || {})
        .filter(([_, v]) =>
          !q ||
          (v.title && v.title.includes(q)) ||
          (v.one_line && v.one_line.includes(q))
        )
        .map(([id, v]) => ({ id, ...v }));
      return { cat, meta, entries };
    }).filter((g) => g.entries.length > 0);
  }, [filter]);

  return (
    <div
      dir="rtl"
      data-testid="marketing-glossary-page"
      style={{
        minHeight: "100vh",
        background: "#F7F2EC",
        color: "#3D2A1C",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
        padding: "40px 24px",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <header style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(170,149,102,0.14)", color: "#AA9566",
              fontSize: 11, fontWeight: 600,
            }}>PREVIEW</span>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700,
                          letterSpacing: -0.6 }}>
              قاموس المؤشرات التسويقية
            </h1>
          </div>
          <p style={{ marginTop: 10, fontSize: 14, color: "#78716C" }}>
            كل رقم في «ثواني» بلغة عربية بسيطة — تعريفه، ومن أين يُحسب،
            وكيف تستفيد منه. اضغط أي بطاقة لعرض الشرح الكامل.
          </p>
        </header>

        <div style={{
          background: "#FFFFFF", borderRadius: 14, padding: "12px 14px",
          marginBottom: 20, border: "1px solid rgba(61,42,28,0.14)",
          boxShadow: "0 4px 16px rgba(61,42,28,0.06)",
          display: "flex", gap: 10, alignItems: "center",
        }}>
          <span style={{ fontSize: 18 }}>{"\u{1F50D}"}</span>
          <input
            data-testid="glossary-search"
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="ابحث عن مؤشر (مثال: الفاتورة، الاحتفاظ، الشرائح)…"
            style={{
              flex: 1, border: "none", outline: "none", fontSize: 14,
              fontFamily: "inherit", background: "transparent", color: "#3D2A1C",
            }}
          />
        </div>

        {groups.length === 0 && (
          <div data-testid="glossary-empty" style={{
            padding: 40, textAlign: "center", color: "#78716C",
            background: "#FFFFFF", borderRadius: 16,
            border: "1px dashed rgba(170,149,102,0.35)",
          }}>
            لا يوجد مؤشر يطابق «{filter}» بعد. جرّب كلمة أخرى.
          </div>
        )}

        {groups.map((g) => (
          <section
            key={g.cat}
            data-testid={`glossary-group-${g.cat}`}
            style={{ marginBottom: 28 }}
          >
            <h2 style={{
              margin: "0 0 12px 0", fontSize: 18, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 8,
              color: "#3D2A1C",
            }}>
              <span>{g.meta.emoji}</span>
              <span>{g.meta.label}</span>
              <span style={{
                fontSize: 12, color: "#A8A29E", fontWeight: 500, marginInlineStart: 8,
              }}>({g.entries.length})</span>
            </h2>
            <ul style={{
              listStyle: "none", padding: 0, margin: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}>
              {g.entries.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    data-testid={`glossary-item-${g.cat}-${e.id}`}
                    onClick={() => setOpen({ cat: g.cat, id: e.id })}
                    style={{
                      width: "100%", cursor: "pointer", textAlign: "start",
                      background: "#FFFFFF",
                      border: `1px solid ${e.color || "#D5C19E"}55`,
                      borderRadius: 14, padding: "16px 18px",
                      color: "#3D2A1C", boxShadow: "0 4px 14px rgba(61,42,28,0.06)",
                      transition: "transform 150ms ease, box-shadow 150ms ease",
                      display: "flex", flexDirection: "column", gap: 8,
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(ev) => {
                      ev.currentTarget.style.transform = "translateY(-2px)";
                      ev.currentTarget.style.boxShadow = "0 8px 22px rgba(61,42,28,0.12)";
                    }}
                    onMouseLeave={(ev) => {
                      ev.currentTarget.style.transform = "translateY(0)";
                      ev.currentTarget.style.boxShadow = "0 4px 14px rgba(61,42,28,0.06)";
                    }}
                  >
                    <span style={{
                      fontSize: 15, fontWeight: 700, color: e.color || "#3D2A1C",
                    }}>{e.title}</span>
                    <span style={{
                      fontSize: 12.5, color: "#57534E", lineHeight: 1.6,
                    }}>{e.one_line}</span>
                    <span style={{
                      marginTop: 4, fontSize: 11, color: "#A8A29E",
                    }}>اضغط للشرح الكامل ←</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <footer style={{
          marginTop: 40, paddingTop: 20,
          borderTop: "1px solid rgba(61,42,28,0.12)",
          fontSize: 12, color: "#A8A29E", textAlign: "center",
        }}>
          Track B · مسار معاينة مخفي — القاموس المركزي يستخدم نفس المكوّن
          الذي يعمل داخل شاشة الشرائح
        </footer>

        <MetricExplainer
          category={open.cat}
          metricId={open.id}
          isOpen={!!(open.cat && open.id)}
          onClose={() => setOpen({ cat: null, id: null })}
        />
      </div>
    </div>
  );
}
